import { notification } from "antd";
import { get } from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import {
  getBlockingPersonName,
  hasFirstAndLastName,
  isHouseholdBlocked,
} from "./utils";

const PPEGELContext = createContext(null);

const VIEWS = {
  LIST: "list",
  DETAIL: "detail",
};

const ACTIONS = {
  SET_PERSONS: "SET_PERSONS",
  SELECT_PERSON: "SELECT_PERSON",
  SELECT_ALERT: "SELECT_ALERT",
  BACK_TO_LIST: "BACK_TO_LIST",
  SET_LOADING: "SET_LOADING",
  SET_DRAWER_OPEN: "SET_DRAWER_OPEN",
  UPDATE_FROM_PROJECT: "UPDATE_FROM_PROJECT",
};

const initialState = {
  persons: [],
  selectedPersonIndex: 0,
  selectedAlertIndex: null,
  view: VIEWS.LIST,
  isBlocked: false,
  blockedByName: null,
  loading: false,
  drawerOpen: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_PERSONS:
      return {
        ...state,
        persons: action.payload,
        isBlocked: isHouseholdBlocked(action.payload),
        blockedByName: getBlockingPersonName(action.payload),
      };
    case ACTIONS.SELECT_PERSON:
      return {
        ...state,
        selectedPersonIndex: action.payload,
        selectedAlertIndex: null,
        view: VIEWS.LIST,
      };
    case ACTIONS.SELECT_ALERT:
      return {
        ...state,
        selectedAlertIndex: action.payload,
        view: VIEWS.DETAIL,
      };
    case ACTIONS.BACK_TO_LIST:
      return {
        ...state,
        selectedAlertIndex: null,
        view: VIEWS.LIST,
      };
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    case ACTIONS.SET_DRAWER_OPEN:
      return {
        ...state,
        drawerOpen: action.payload,
        ...(action.payload === false
          ? { selectedAlertIndex: null, view: VIEWS.LIST }
          : {}),
      };
    case ACTIONS.UPDATE_FROM_PROJECT: {
      const persons = (
        get(action.payload, "fields.efficialAlerts.persons", []) || []
      ).filter(hasFirstAndLastName);
      return {
        ...state,
        persons,
        isBlocked: isHouseholdBlocked(persons),
        blockedByName: getBlockingPersonName(persons),
        loading: false,
      };
    }
    default:
      return state;
  }
};

const PPEGELProvider = ({
  projectId,
  subscription,
  children,
  onBlockedStatusChange,
  enabled = true,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const personFingerprint = useMemo(() => {
    const contact = get(subscription, "project.contact", {});
    const relatedPersons = get(contact, "relatedPersons", []) || [];
    return JSON.stringify({
      c: {
        fn: get(contact, "user.firstname"),
        ln: get(contact, "user.lastname"),
      },
      rp: relatedPersons.map((rp) => ({
        fn: get(rp, "identity.user.firstname"),
        ln: get(rp, "identity.user.lastname"),
      })),
    });
  }, [subscription]);

  const [efficialCheck] = useMutation(graphql.mutations.EFFICIAL_CHECK, {
    fetchPolicy: "no-cache",
    onCompleted: ({ efficialCheck: project }) => {
      dispatch({ type: ACTIONS.UPDATE_FROM_PROJECT, payload: project });
    },
    onError: () => {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    },
  });

  const [dismissAlert] = useMutation(graphql.mutations.EFFICIAL_DISMISS_ALERT, {
    fetchPolicy: "network-only",
    onCompleted: ({ efficialDismissAlert: project }) => {
      dispatch({ type: ACTIONS.UPDATE_FROM_PROJECT, payload: project });
      notification.success({ message: "Alerte levée avec succès" });
    },
  });

  const [confirmAlert] = useMutation(graphql.mutations.EFFICIAL_CONFIRM_ALERT, {
    fetchPolicy: "network-only",
    onCompleted: ({ efficialConfirmAlert: project }) => {
      dispatch({ type: ACTIONS.UPDATE_FROM_PROJECT, payload: project });
      notification.warning({ message: "Alerte confirmée" });
    },
  });

  const [resetAlert] = useMutation(graphql.mutations.EFFICIAL_RESET_ALERT, {
    fetchPolicy: "network-only",
    onCompleted: ({ efficialResetAlert: project }) => {
      dispatch({ type: ACTIONS.UPDATE_FROM_PROJECT, payload: project });
      notification.info({ message: "Action annulée" });
    },
  });

  useEffect(() => {
    if (!enabled || !projectId) {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
      if (!enabled) {
        dispatch({ type: ACTIONS.SET_PERSONS, payload: [] });
      }
      return;
    }
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    efficialCheck({ variables: { where: { id: projectId } } });
  }, [enabled, projectId, personFingerprint, efficialCheck]);

  useEffect(() => {
    if (onBlockedStatusChange) {
      onBlockedStatusChange(state.isBlocked);
    }
  }, [state.isBlocked, onBlockedStatusChange]);

  const handleLever = useCallback(
    (alertId, personId) => {
      dismissAlert({
        variables: { where: { projectId, alertId, personId } },
      });
    },
    [projectId, dismissAlert],
  );

  const handleConfirmer = useCallback(
    (alertId, personId) => {
      confirmAlert({
        variables: { where: { projectId, alertId, personId } },
      });
    },
    [projectId, confirmAlert],
  );

  const handleAnnuler = useCallback(
    (alertId, personId) => {
      resetAlert({
        variables: { where: { projectId, alertId, personId } },
      });
    },
    [projectId, resetAlert],
  );

  const selectPerson = useCallback((index) => {
    dispatch({ type: ACTIONS.SELECT_PERSON, payload: index });
  }, []);

  const selectAlert = useCallback((index) => {
    dispatch({ type: ACTIONS.SELECT_ALERT, payload: index });
  }, []);

  const backToList = useCallback(() => {
    dispatch({ type: ACTIONS.BACK_TO_LIST });
  }, []);

  const openDrawer = useCallback(() => {
    dispatch({ type: ACTIONS.SET_DRAWER_OPEN, payload: true });
  }, []);

  const closeDrawer = useCallback(() => {
    dispatch({ type: ACTIONS.SET_DRAWER_OPEN, payload: false });
  }, []);

  const value = {
    ...state,
    handleLever,
    handleConfirmer,
    handleAnnuler,
    selectPerson,
    selectAlert,
    backToList,
    openDrawer,
    closeDrawer,
  };

  return (
    <PPEGELContext.Provider value={value}>{children}</PPEGELContext.Provider>
  );
};

export const usePPEGEL = () => {
  const context = useContext(PPEGELContext);
  if (!context) {
    throw new Error("usePPEGEL must be used within a PPEGELProvider");
  }
  return context;
};

export { VIEWS };
export default PPEGELProvider;
